import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { usePeriodSettings } from '@/hooks/usePeriodSettings';
import {
  fetchGroupExpenses,
  fetchExpenseDetail,
  createExpense,
  updateExpense,
  deleteExpense,
  fetchAllExpenses,
  batchDeleteExpenses,
  batchUpdateCategory,
  ALL_EXPENSES_PAGE_SIZE,
} from '@/api/expenses';
import type { AllExpensesFilters, AllExpensesResult } from '@/api/expenses';
import type { CreateExpenseData, SplitType } from '@sharecost/shared';
import { Analytics } from '@/services/analytics.service';

export function useGroupExpenses(groupId: string | undefined) {
  return useQuery({
    queryKey: ['expenses', groupId],
    queryFn: () => fetchGroupExpenses(groupId!),
    enabled: !!groupId,
  });
}

export function useExpenseDetail(expenseId: string | undefined) {
  return useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => fetchExpenseDetail(expenseId!),
    enabled: !!expenseId,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExpenseData) => createExpense(data),
    onSuccess: (expenseId, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['balances', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
      Analytics.track({
        name: 'Expense Created',
        properties: {
          expense_id: expenseId,
          group_id: variables.groupId,
          amount: variables.amount,
          currency: variables.currencyCode,
          split_type: variables.splitType,
          member_count: variables.splitWith.length,
          has_receipt: !!variables.receipt_url,
          has_description: !!variables.description,
        },
      });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      expenseId: string;
      groupId: string;
      updates: {
        description?: string;
        amount?: number;
        paidBy?: string;
        categoryId?: string;
        date?: string;
        splitType?: SplitType;
        splitWith?: string[];
        exactAmounts?: Record<string, number>;
        percentages?: Record<string, number>;
        shares?: Record<string, number>;
      };
    }) => updateExpense(data.expenseId, data.groupId, data.updates),
    onSuccess: (_, { expenseId, groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['expense', expenseId] });
      queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
      queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
      Analytics.track({
        name: 'Expense Updated',
        properties: { expense_id: expenseId, group_id: groupId, fields_changed: [] },
      });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, groupId }: { expenseId: string; groupId: string }) =>
      deleteExpense(expenseId),
    onSuccess: (_, { expenseId, groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['expenses', groupId] });
      queryClient.invalidateQueries({ queryKey: ['balances', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget', groupId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
      Analytics.track({
        name: 'Expense Deleted',
        properties: { expense_id: expenseId, group_id: groupId },
      });
    },
  });
}

// --- Cross-group expenses ---

export function useAllExpenses(filters: AllExpensesFilters, scopeGroupIds?: string[]) {
  // Date presets depend on the user's week/month start, so they are part of the key
  const periodSettings = usePeriodSettings();

  return useInfiniteQuery<AllExpensesResult>({
    queryKey: ['all-expenses', filters, scopeGroupIds, periodSettings],
    queryFn: ({ pageParam }) =>
      fetchAllExpenses(filters, pageParam as number, ALL_EXPENSES_PAGE_SIZE, scopeGroupIds, periodSettings),
    // Wait until the non-archived group list is known, so archived-group
    // expenses are never shown (even briefly) before scoping is applied.
    enabled: scopeGroupIds !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.expenses.length, 0);
      if (loaded >= lastPage.totalCount) return undefined;
      return loaded;
    },
  });
}

export function useBatchDeleteExpenses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => batchDeleteExpenses(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['groupBudget'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-budgets'] });
    },
  });
}

export function useBatchUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, categoryId }: { ids: string[]; categoryId: string | null }) =>
      batchUpdateCategory(ids, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}
